
import React from "react";
import { useTranslation } from "react-i18next";
import { MessageSquare, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SocialGroupType } from "@/types/social-groups";

interface ViewGroupDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  group: SocialGroupType | null;
  isRTL: boolean;
}

export const ViewGroupDialog: React.FC<ViewGroupDialogProps> = ({
  isOpen,
  setIsOpen,
  group,
  isRTL,
}) => {
  const { t } = useTranslation();

  if (!group) return null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>
            {group?.name[isRTL ? "ar" : "en"]}
          </DialogTitle>
        </DialogHeader>
          
        <div className="space-y-4">
          <div className="flex justify-center mb-4">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
              group.type === "whatsapp" ? "bg-green-100" : "bg-blue-100"
            }`}>
              {group.type === "whatsapp" ? (
                <MessageSquare size={32} className="text-green-600" />
              ) : (
                <Send size={32} className="text-blue-600" />
              )}
            </div>
          </div>
          
          <div className="border rounded-lg p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <div className="text-sm text-muted-foreground">
                  {isRTL ? "الاسم (عربي)" : "Name (Arabic)"}
                </div>
                <div className="font-medium">{group.name.ar}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">
                  {isRTL ? "الاسم (إنجليزي)" : "Name (English)"}
                </div>
                <div className="font-medium">{group.name.en}</div>
              </div>
            </div>
          </div>
          
          <div className="border rounded-lg p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <div className="text-sm text-muted-foreground">
                  {isRTL ? "الوصف (عربي)" : "Description (Arabic)"}
                </div>
                <div>{group.description.ar}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">
                  {isRTL ? "الوصف (إنجليزي)" : "Description (English)"}
                </div>
                <div>{group.description.en}</div>
              </div>
            </div>
          </div>
          
          <div className="border rounded-lg p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <div className="text-sm text-muted-foreground">
                  {isRTL ? "نوع المجموعة" : "Group Type"}
                </div>
                <div className="font-medium flex items-center gap-2">
                  {group.type === "whatsapp" ? (
                    <>
                      <MessageSquare size={16} className="text-green-500" />
                      <span>{isRTL ? "واتساب" : "WhatsApp"}</span>
                    </>
                  ) : (
                    <>
                      <Send size={16} className="text-blue-500" />
                      <span>{isRTL ? "تلجرام" : "Telegram"}</span>
                    </>
                  )}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">
                  {isRTL ? "عدد الأعضاء" : "Members Count"}
                </div>
                <div className="font-medium">
                  {group.members.toLocaleString()}
                </div>
              </div>
            </div>
          </div>
          
          <div className="border rounded-lg p-4">
            <div className="text-sm text-muted-foreground mb-1">
              {isRTL ? "رابط المجموعة" : "Group Link"}
            </div>
            <div className="text-blue-600 break-all">
              <a href={group.link} target="_blank" rel="noopener noreferrer" className="hover:underline">
                {group.link}
              </a>
            </div>
          </div>
          
          <div className="flex justify-center">
            <Button onClick={() => window.open(group.link, "_blank")} className={`${
              group.type === "whatsapp" ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"
            }`}>
              {isRTL ? "الانضمام للمجموعة" : "Join Group"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
